import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { Shield, TrendingUp, Landmark, PiggyBank, Banknote, Building2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_OPTIONS = [
  { key: 'insurance', label: 'Insurance Policy', icon: Shield, tint: 'text-blue-700', hint: 'Life / General insurance product' },
  { key: 'mutual_fund', label: 'Mutual Fund', icon: TrendingUp, tint: 'text-emerald-700', hint: 'SIP or lump-sum investment' },
  { key: 'loan', label: 'Loan', icon: Landmark, tint: 'text-orange-700', hint: 'Home / Personal / Vehicle / Gold' },
  { key: 'recurring_deposit', label: 'Recurring Deposit', icon: PiggyBank, tint: 'text-teal-700', hint: 'Monthly bank RD' },
  { key: 'fixed_deposit', label: 'Fixed Deposit', icon: Banknote, tint: 'text-indigo-700', hint: 'Bank/Post office FD' },
  { key: 'ppf', label: 'PPF', icon: Building2, tint: 'text-purple-700', hint: 'Public Provident Fund' },
  { key: 'post_office', label: 'Post Office Scheme', icon: Building2, tint: 'text-slate-700', hint: 'MIS/NSC/KVP etc.' },
];

const LOAN_SUB_TYPES = ['Home Loan', 'Personal Loan', 'Vehicle Loan', 'Gold Loan'];

const addDaysISO = (isoDate, days) => {
  if (!isoDate) return '';
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) return '';
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

/**
 * "Add Product" dialog — supports Insurance (delegates to AddPolicyDialog
 * flow via `onRequestInsurance`) plus Mutual Fund, Loan, RD, FD, PPF and
 * Post Office schemes.
 */
const AddProductDialog = ({ open, onClose, initialClientId = '', initialCategory = '', onCreated, onRequestInsurance }) => {
  const [category, setCategory] = useState(initialCategory);
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState({
    client_id: initialClientId, name: '', sub_type: '', amount: '',
    lump_sum_or_sip: 'SIP', folio_number: '', account_number: '',
    tenure_months: '', interest_rate: '', emi: '',
    start_date: '', maturity_date: '', notes: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setCategory(initialCategory);
    setForm(f => ({ ...f, client_id: initialClientId || '' }));
    axios.get(`${API}/clients`, { withCredentials: true })
      .then(r => setClients(r.data))
      .catch(err => console.warn('Clients load failed:', err?.message));
  }, [open, initialClientId, initialCategory]);

  const update = (patch) => setForm(f => ({ ...f, ...patch }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.client_id) { toast.error('Please select a client'); return; }
    if (!category) { toast.error('Choose a product category'); return; }
    setSaving(true);
    try {
      const payload = {
        client_id: form.client_id,
        category,
        name: form.name || null,
        sub_type: form.sub_type || null,
        amount: form.amount === '' ? null : parseFloat(form.amount),
        lump_sum_or_sip: category === 'mutual_fund' ? (form.lump_sum_or_sip || null) : null,
        folio_number: form.folio_number || null,
        account_number: form.account_number || null,
        tenure_months: form.tenure_months === '' ? null : parseInt(form.tenure_months, 10),
        interest_rate: form.interest_rate === '' ? null : parseFloat(form.interest_rate),
        emi: form.emi === '' ? null : parseFloat(form.emi),
        start_date: form.start_date || null,
        maturity_date: form.maturity_date || null,
        notes: form.notes || null,
      };
      const { data } = await axios.post(`${API}/financial-products`, payload, { withCredentials: true });
      toast.success('Product added to client profile');
      if (onCreated) onCreated(data);
      setForm({
        client_id: '', name: '', sub_type: '', amount: '', lump_sum_or_sip: 'SIP',
        folio_number: '', account_number: '', tenure_months: '', interest_rate: '',
        emi: '', start_date: '', maturity_date: '', notes: '',
      });
      setCategory('');
      onClose();
    } catch (err) {
      console.error('Create product failed:', err);
      toast.error(err.response?.data?.detail || 'Failed to add product');
    } finally { setSaving(false); }
  };

  // Auto-fill maturity from start + tenure_months for RD/FD/PPF etc.
  const onStartChange = (v) => {
    setForm(f => {
      const t = parseInt(f.tenure_months || '0', 10);
      const nextMaturity = t > 0 && v ? addDaysISO(v, t * 30) : f.maturity_date;
      return { ...f, start_date: v, maturity_date: nextMaturity || f.maturity_date };
    });
  };

  const chooseInsurance = () => {
    if (onRequestInsurance) onRequestInsurance({ clientId: form.client_id });
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="add-product-dialog">
        <DialogHeader>
          <DialogTitle>Add Product</DialogTitle>
          <DialogDescription>Choose the product category, then fill in the details. All product types link to a client.</DialogDescription>
        </DialogHeader>

        {!category && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2" data-testid="product-category-grid">
            {CATEGORY_OPTIONS.map(({ key, label, icon: Icon, tint, hint }) => (
              <button
                key={key}
                type="button"
                onClick={() => key === 'insurance' ? chooseInsurance() : setCategory(key)}
                className="flex flex-col items-start gap-1 p-3 rounded-lg border border-slate-200 hover:border-blue-400 hover:shadow-sm bg-white transition-all text-left"
                data-testid={`product-category-${key}`}
              >
                <Icon className={`h-5 w-5 ${tint}`} />
                <p className="text-sm font-semibold text-slate-900">{label}</p>
                <p className="text-[11px] text-slate-500">{hint}</p>
              </button>
            ))}
          </div>
        )}

        {category && (
          <form onSubmit={submit} className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-slate-700">
                Category: <strong>{CATEGORY_OPTIONS.find(c => c.key === category)?.label}</strong>
              </p>
              <Button type="button" variant="ghost" size="sm" onClick={() => setCategory('')} data-testid="product-change-category">
                Change
              </Button>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 space-y-2">
                <Label>Client *</Label>
                <Select value={form.client_id} onValueChange={(v) => update({ client_id: v })}>
                  <SelectTrigger data-testid="product-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
                  <SelectContent>
                    {clients.map(c => <SelectItem key={c.client_id} value={c.client_id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              {category === 'mutual_fund' && (
                <>
                  <div className="space-y-2"><Label>Fund / AMC *</Label>
                    <Input value={form.name} onChange={(e) => update({ name: e.target.value })} required data-testid="mf-name-input" />
                  </div>
                  <div className="space-y-2"><Label>Mode *</Label>
                    <Select value={form.lump_sum_or_sip} onValueChange={(v) => update({ lump_sum_or_sip: v })}>
                      <SelectTrigger data-testid="mf-mode-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="SIP">SIP (Monthly)</SelectItem>
                        <SelectItem value="Lump Sum">Lump Sum</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2"><Label>Amount (₹) *</Label>
                    <Input type="number" min="0" value={form.amount} onChange={(e) => update({ amount: e.target.value })} required data-testid="mf-amount-input" />
                  </div>
                  <div className="space-y-2"><Label>Folio Number</Label>
                    <Input value={form.folio_number} onChange={(e) => update({ folio_number: e.target.value })} data-testid="mf-folio-input" />
                  </div>
                  <div className="space-y-2"><Label>Start Date</Label>
                    <Input type="date" value={form.start_date} onChange={(e) => update({ start_date: e.target.value })} data-testid="mf-start-input" />
                  </div>
                </>
              )}

              {category === 'loan' && (
                <>
                  <div className="space-y-2"><Label>Loan Type *</Label>
                    <Select value={form.sub_type} onValueChange={(v) => update({ sub_type: v })}>
                      <SelectTrigger data-testid="loan-subtype-select"><SelectValue placeholder="Pick loan type" /></SelectTrigger>
                      <SelectContent>
                        {LOAN_SUB_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2"><Label>Lender Name *</Label>
                    <Input value={form.name} onChange={(e) => update({ name: e.target.value })} required data-testid="loan-lender-input" />
                  </div>
                  <div className="space-y-2"><Label>Loan Amount (₹) *</Label>
                    <Input type="number" min="0" value={form.amount} onChange={(e) => update({ amount: e.target.value })} required data-testid="loan-amount-input" />
                  </div>
                  <div className="space-y-2"><Label>Tenure (months)</Label>
                    <Input type="number" min="0" value={form.tenure_months} onChange={(e) => update({ tenure_months: e.target.value })} data-testid="loan-tenure-input" />
                  </div>
                  <div className="space-y-2"><Label>Interest Rate (%)</Label>
                    <Input type="number" step="0.01" min="0" value={form.interest_rate} onChange={(e) => update({ interest_rate: e.target.value })} data-testid="loan-rate-input" />
                  </div>
                  <div className="space-y-2"><Label>EMI (₹)</Label>
                    <Input type="number" min="0" value={form.emi} onChange={(e) => update({ emi: e.target.value })} data-testid="loan-emi-input" />
                  </div>
                  <div className="space-y-2"><Label>Start Date</Label>
                    <Input type="date" value={form.start_date} onChange={(e) => update({ start_date: e.target.value })} data-testid="loan-start-input" />
                  </div>
                </>
              )}

              {(category === 'recurring_deposit' || category === 'fixed_deposit') && (
                <>
                  <div className="space-y-2"><Label>Institution *</Label>
                    <Input value={form.name} onChange={(e) => update({ name: e.target.value })} required data-testid="deposit-institution-input" />
                  </div>
                  <div className="space-y-2"><Label>{category === 'recurring_deposit' ? 'Monthly Deposit' : 'Deposit Amount'} (₹) *</Label>
                    <Input type="number" min="0" value={form.amount} onChange={(e) => update({ amount: e.target.value })} required data-testid="deposit-amount-input" />
                  </div>
                  <div className="space-y-2"><Label>Tenure (months) *</Label>
                    <Input type="number" min="0" value={form.tenure_months} onChange={(e) => update({ tenure_months: e.target.value })} required data-testid="deposit-tenure-input" />
                  </div>
                  <div className="space-y-2"><Label>Interest Rate (%)</Label>
                    <Input type="number" step="0.01" min="0" value={form.interest_rate} onChange={(e) => update({ interest_rate: e.target.value })} data-testid="deposit-rate-input" />
                  </div>
                  <div className="space-y-2"><Label>Start Date *</Label>
                    <Input type="date" value={form.start_date} onChange={(e) => onStartChange(e.target.value)} required data-testid="deposit-start-input" />
                  </div>
                  <div className="space-y-2"><Label>Maturity Date</Label>
                    <Input type="date" value={form.maturity_date} onChange={(e) => update({ maturity_date: e.target.value })} data-testid="deposit-maturity-input" />
                    <p className="text-[10px] text-slate-500">Auto-fills from start + tenure. Edit if the actual maturity differs.</p>
                  </div>
                </>
              )}

              {(category === 'ppf' || category === 'post_office') && (
                <>
                  <div className="space-y-2"><Label>Institution *</Label>
                    <Input value={form.name} onChange={(e) => update({ name: e.target.value })} required placeholder={category === 'ppf' ? 'Bank or Post Office branch' : 'Post Office branch / scheme'} data-testid="ppf-institution-input" />
                  </div>
                  <div className="space-y-2"><Label>Account Number</Label>
                    <Input value={form.account_number} onChange={(e) => update({ account_number: e.target.value })} data-testid="ppf-account-input" />
                  </div>
                  <div className="space-y-2"><Label>Annual Contribution (₹) *</Label>
                    <Input type="number" min="0" value={form.amount} onChange={(e) => update({ amount: e.target.value })} required data-testid="ppf-amount-input" />
                  </div>
                  <div className="space-y-2"><Label>Start Date</Label>
                    <Input type="date" value={form.start_date} onChange={(e) => update({ start_date: e.target.value })} data-testid="ppf-start-input" />
                  </div>
                  <div className="space-y-2"><Label>Maturity Date</Label>
                    <Input type="date" value={form.maturity_date} onChange={(e) => update({ maturity_date: e.target.value })} data-testid="ppf-maturity-input" />
                  </div>
                </>
              )}

              <div className="col-span-2 space-y-2">
                <Label>Notes</Label>
                <Input value={form.notes} onChange={(e) => update({ notes: e.target.value })} data-testid="product-notes-input" />
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
              <Button type="submit" className="bg-blue-800 hover:bg-blue-900" disabled={saving} data-testid="product-submit-button">
                {saving ? 'Adding…' : 'Add Product'}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AddProductDialog;
